import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useCart } from '../hooks/useCart';

interface CartLine {
  id: string;
  merchandiseId: string;
  quantity: number;
  merchandise: {
    id: string;
    title: string;
    product: {
      title: string;
      featuredImage?: {
        url: string;
      };
    };
    priceV2: {
      amount: string;
      currencyCode: string;
    };
  };
}

interface Cart {
  id: string;
  checkoutUrl: string;
  lines: {
    edges: Array<{
      node: CartLine;
    }>;
  };
  cost: {
    totalAmount: {
      amount: string;
      currencyCode: string;
    };
    subtotalAmount: {
      amount: string;
      currencyCode: string;
    };
  };
}

interface ShopifyContextType {
  cart: Cart | null;
  loading: boolean;
  error: string | null;
  isCartOpen: boolean;
  addToCart: (variantId: string, quantity?: number) => Promise<void>;
  updateCartLine: (lineId: string, quantity: number) => Promise<void>;
  removeFromCart: (lineId: string) => Promise<void>;
  openCart: () => void;
  closeCart: () => void;
  itemCount: number;
}

const ShopifyContext = createContext<ShopifyContextType | undefined>(undefined);

export const ShopifyProvider = ({ children }: { children: ReactNode }) => {
  const { cart, loading, error, addToCart: addToCartHook, updateCartLine: updateCartLineHook, removeFromCart: removeFromCartHook, fetchCart } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Fetch cart on mount
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (variantId: string, quantity: number = 1) => {
    await addToCartHook(variantId, quantity);
    setIsCartOpen(true);
  };

  const updateCartLine = async (lineId: string, quantity: number) => {
    await updateCartLineHook(lineId, quantity);
  };

  const removeFromCart = async (lineId: string) => {
    await removeFromCartHook(lineId);
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const itemCount = cart?.lines.edges.reduce((sum, { node }) => sum + node.quantity, 0) || 0;

  return (
    <ShopifyContext.Provider
      value={{
        cart,
        loading,
        error,
        isCartOpen,
        addToCart,
        updateCartLine,
        removeFromCart,
        openCart,
        closeCart,
        itemCount,
      }}
    >
      {children}
    </ShopifyContext.Provider>
  );
};

export const useShopify = () => {
  const context = useContext(ShopifyContext);
  if (!context) throw new Error('useShopify must be used within a ShopifyProvider');
  return context;
};